'use client'

import { useState, useMemo } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

const SUPPORTED_TIMEZONES =
  typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function'
    ? [...new Set(['UTC', ...Intl.supportedValuesOf('timeZone')])].sort((a, b) => a.localeCompare(b))
    : ['UTC']

type TimezoneOption = {
  value: string
  label: string
  abbreviation: string
  offset: string
  offsetMinutes: number
  searchText: string
}

const getOffsetMinutes = (date: Date, timeZone: string) => {
  try {
    const localeString = date.toLocaleString('en-US', { timeZone, hour12: false })
    const localTime = new Date(localeString)
    const utcString = date.toLocaleString('en-US', { timeZone: 'UTC', hour12: false })
    const utcTime = new Date(utcString)
    return Math.round((localTime.getTime() - utcTime.getTime()) / 60000)
  } catch {
    return 0
  }
}

const formatOffset = (minutes: number) => {
  const sign = minutes >= 0 ? '+' : '-'
  const absMinutes = Math.abs(minutes)
  const hours = Math.floor(absMinutes / 60)
  const mins = absMinutes % 60
  return `${sign}${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

const createTimezoneOption = (timeZone: string, referenceDate: Date): TimezoneOption | null => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    const parts = formatter.formatToParts(referenceDate)
    const tzName = parts.find((part) => part.type === 'timeZoneName')?.value
    const abbreviation = tzName && tzName !== timeZone ? tzName : timeZone
    const offsetMinutes = getOffsetMinutes(referenceDate, timeZone)
    const offset = formatOffset(offsetMinutes)

    return {
      value: timeZone,
      label: `${timeZone} (${abbreviation})`,
      abbreviation,
      offset,
      offsetMinutes,
      searchText: `${timeZone.toLowerCase()} ${abbreviation.toLowerCase()} ${offset.toLowerCase()}`,
    }
  } catch {
    return null
  }
}

const sortTimezoneOptions = (options: TimezoneOption[]) =>
  [...options].sort((a, b) => {
    if (a.offsetMinutes === b.offsetMinutes) {
      return a.value.localeCompare(b.value)
    }
    return a.offsetMinutes - b.offsetMinutes
  })

const buildTimezoneOptions = () => {
  const referenceDate = new Date()
  const candidates = SUPPORTED_TIMEZONES.length > 0 ? SUPPORTED_TIMEZONES : ['UTC']

  const options = candidates
    .map((timeZone) => createTimezoneOption(timeZone, referenceDate))
    .filter((option): option is TimezoneOption => option !== null)

  if (options.length === 0) {
    const fallback = createTimezoneOption('UTC', referenceDate)
    return fallback ? [fallback] : []
  }

  return sortTimezoneOptions(options)
}

interface TimezoneSelectProps {
  value: string
  onChange: (value: string) => void
  className?: string
  id?: string
  describedBy?: string
}

export function TimezoneSelect({ value, onChange, className, id, describedBy }: TimezoneSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const baseOptions = useMemo<TimezoneOption[]>(() => buildTimezoneOptions(), [])

  const options = useMemo(() => {
    if (!value) return baseOptions
    if (baseOptions.some((option) => option.value === value)) return baseOptions

    const customOption = createTimezoneOption(value, new Date())
    if (!customOption) return baseOptions

    return sortTimezoneOptions([...baseOptions, customOption])
  }, [baseOptions, value])

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return options
    return options.filter((option) => option.searchText.includes(query))
  }, [options, search])

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value]
  )

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          aria-describedby={describedBy}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            !selectedOption && 'text-muted-foreground',
            className
          )}
        >
          <span className="line-clamp-1 text-xs">
            {selectedOption ? selectedOption.label : 'Select timezone'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        <Command shouldFilter={false}>
          <div className="border-b border-border bg-popover">
            <CommandInput
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder="Search timezone..."
            />
          </div>
          <CommandList className="max-h-64 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              <CommandGroup className="p-0">
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onChange(option.value)
                      setSearch('')
                      setIsOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        option.value === value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="text-xs line-clamp-1">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              <div className="px-3 py-4 text-sm text-muted-foreground">No matching timezones</div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
